import { ChevronLeft, ChevronRight, FileText, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import type { LogEntry } from './useLiveExecution';

interface LogsPanelProps {
  logs: LogEntry[];
  expanded: boolean;
  onToggle: () => void;
  nodeLabel?: string;
}

export function LogsPanel({ logs, expanded, onToggle, nodeLabel }: LogsPanelProps) {
  const errorCount = logs.filter(log => log.level === 'error').length;
  const warnCount = logs.filter(log => log.level === 'warn').length;
  const hasIssues = errorCount > 0 || warnCount > 0;

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp).toLocaleTimeString('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  const getLevelColor = (level: LogEntry['level']) => {
    switch (level) {
      case 'error':
        return 'text-red-500';
      case 'warn':
        return 'text-yellow-500';
      case 'info':
        return 'text-blue-500';
      default:
        return 'text-muted-foreground';
    }
  };

  return (
    <div
      className={cn(
        'absolute right-0 top-0 bottom-0 transition-all duration-300 ease-in-out bg-background border-l flex flex-col z-10',
        expanded ? 'w-[50%]' : 'w-10'
      )}
    >
      {expanded ? (
        // Expanded view - full logs panel
        <>
          {/* Header */}
          <div className="border-b px-4 py-2 flex items-center justify-between flex-shrink-0">
            <div className="flex flex-col gap-0.5">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold">Logs</h3>
                {logs.length > 0 && (
                  <Badge variant="secondary" className="text-xs">
                    {logs.length}
                  </Badge>
                )}
              </div>
              {nodeLabel && logs.length > 0 && (
                <span className="text-xs text-muted-foreground">for {nodeLabel}</span>
              )}
            </div>
            <button
              onClick={onToggle}
              className="p-1 hover:bg-accent rounded transition-colors"
              title="Collapse logs"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>

          {/* Logs Content */}
          <div className="flex-1 overflow-y-auto p-4">
            {logs.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-muted-foreground text-sm">
                  <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>No logs available</p>
                </div>
              </div>
            ) : (
              <div className="space-y-1 font-mono text-xs">
                {logs.map((log) => (
                  <div
                    key={log.id}
                    className="py-1.5 px-2 rounded hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <span className="text-muted-foreground whitespace-nowrap">
                        {formatTimestamp(log.timestamp)}
                      </span>
                      <span
                        className={cn(
                          'font-semibold uppercase text-[10px] mt-0.5 whitespace-nowrap',
                          getLevelColor(log.level)
                        )}
                      >
                        [{log.level}]
                      </span>
                      <span
                        className="break-words flex-1"
                        style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
                      >
                        {log.message}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      ) : (
        // Collapsed view - thin vertical strip
        <button
          onClick={onToggle}
          className="flex flex-col items-center justify-center h-full w-full hover:bg-accent transition-colors"
          title="Expand logs"
        >
          <ChevronLeft className="w-4 h-4 mb-2" />
          <div
            className="text-xs font-medium tracking-wide"
            style={{
              writingMode: 'vertical-rl',
              textOrientation: 'mixed',
              transform: 'rotate(180deg)',
            }}
          >
            Logs
          </div>
          {hasIssues && (
            <div className="mt-2">
              {errorCount > 0 ? (
                <Badge variant="destructive" className="text-[10px] px-1 py-0">
                  {errorCount}
                </Badge>
              ) : warnCount > 0 ? (
                <Badge className="text-[10px] px-1 py-0 bg-yellow-500">
                  {warnCount}
                </Badge>
              ) : null}
            </div>
          )}
        </button>
      )}
    </div>
  );
}
